<template>
  <!--
    7.4f — O RANKING COMO LUGAR, não tabela (D22, problema 6). Era "uma tabela HTML dentro de um
    modal" — cinco colunas de texto atrás de um botão, e a posição do próprio clã só existia se o
    modal fosse aberto. Agora é um QUADRO no cais: as bandeiras penduradas, os cinco do topo, e o
    seu clã sempre visível — destacado se está no topo, anexado no fim se não está.
  -->
  <section class="faixa" :aria-label="$t('clan.rankingTitle')">
    <p class="faixa__rotulo">
      <FishIcon nome="trophy" tamanho="pequeno" />
      {{ $t('clan.rankingTitle') }}
    </p>

    <p v-if="loading" class="faixa__quieto">{{ $t('clan.rankingLoading') }}</p>
    <p v-else-if="error" class="faixa__quieto">{{ error }}</p>

    <ol v-else class="faixa__lista">
      <li
        v-for="cla in visiveis"
        :key="cla.id"
        :class="{ 'faixa__cla--meu': cla.id === meuClaId, 'faixa__cla--ouro': cla.position === 1 }"
        class="faixa__cla"
      >
        <span class="faixa__posicao t-numero">{{ cla.position }}º</span>

        <!--
          A bandeira é o que torna isto um quadro e não uma lista: o clã se
          reconhece de longe pela cara, e é para isso que a bandeira existe
          (D25). Sem ela, uma linha só tem nome e número — que é o que o D22
          chamou de "o pedaço menos jogo do cliente inteiro".
        -->
        <img
          v-if="cla.flag"
          class="faixa__bandeira"
          :src="cla.flag"
          :alt="$t('clan.bandeira.deste', { cla: cla.name })"
        />
        <span v-else class="faixa__sem-bandeira" aria-hidden="true" />

        <span class="faixa__nome">{{ cla.name }}</span>
        <!-- O país vem DEPOIS do nome: a bandeira da frente é a cara do clã, esta diz de onde ele é. -->
        <BandeiraDePais v-if="cla.country" :codigo="cla.country" />
        <!-- A linha do próprio clã fora das 50 não tem nível nem pontuação: ela
             é montada só com a posição. "Nível" sem número seria pior que nada. -->
        <span v-if="cla.level" class="faixa__nivel t-numero">
          {{ $t('clan.nivel.rotulo') }} {{ cla.level }}
        </span>
        <span class="faixa__pontos t-numero">{{ formato(cla.pontuation) }}</span>

        <!--
          Denunciar (D25) fica AQUI porque é aqui que se vê a bandeira de quem
          não se escolheu ver. A moderação existe justamente para esse caso: o
          membro do clã da imagem não vai denunciá-la.
        -->
        <button
          v-if="cla.flag && cla.id !== meuClaId"
          type="button"
          class="faixa__denunciar"
          :title="$t('clan.bandeira.denunciar')"
          @click="abrirDenuncia(cla)"
        >
          <FishIcon nome="exclamation-triangle" tamanho="miudo" />
        </button>
      </li>
    </ol>

    <!-- A denúncia, na própria faixa: um modal para três campos seria mais tela
         do que conteúdo, e afastaria a acusação da imagem acusada. -->
    <form v-if="denunciando" class="faixa__denuncia" @submit.prevent="enviarDenuncia">
      <p class="faixa__denuncia-alvo">
        {{ $t('clan.bandeira.denunciandoO', { cla: denunciando.name }) }}
      </p>

      <select v-model="motivo" class="faixa__campo" :aria-label="$t('clan.bandeira.motivo')">
        <option v-for="m in MOTIVOS" :key="m" :value="m">
          {{ $t(`clan.bandeira.motivos.${m}`) }}
        </option>
      </select>

      <input
        v-model.trim="detalhe"
        type="text"
        maxlength="200"
        class="faixa__campo"
        :placeholder="$t('clan.bandeira.detalhe')"
        :aria-label="$t('clan.bandeira.detalhe')"
      />

      <div class="faixa__denuncia-botoes">
        <FishButton tamanho="pequeno" :desabilitado="enviando" @click="enviarDenuncia">
          {{ $t('clan.bandeira.enviarDenuncia') }}
        </FishButton>
        <button type="button" class="faixa__cancelar" @click="denunciando = null">
          {{ $t('clan.bandeira.fechar') }}
        </button>
      </div>
    </form>

    <!-- O recado fica FORA do formulário: a denúncia aceita fecha o formulário,
         e um "recebemos" dentro dele desapareceria junto. -->
    <p v-if="recado" class="faixa__recado">{{ recado }}</p>
  </section>
</template>

<script>
import * as v2 from '@/core/services/api/v2';
import { FishButton, FishIcon } from '@/shared/ui';
import BandeiraDePais from '@/shared/paises/BandeiraDePais.vue';

/** Os cinco motivos do enum do banco (`clan_flag_reports.reason`). */
const MOTIVOS = ['offensive', 'sexual', 'hateful', 'copyright', 'other'];

export default {
  name: 'ClanRanking',
  components: { BandeiraDePais, FishButton, FishIcon },
  props: {
    /** O id do clã do jogador, para o destaque e o "sempre visível". */
    meuClaId: { type: Number, default: null },
    /**
     * A posição do próprio clã, do `detalhesDoCla`.
     *
     * Existe porque o ranking público tem 50 linhas: o clã de posição 380 não
     * está nelas, e sem este número ele ficaria sem posição — que é metade do
     * problema 6 do D22, e a metade que tinha sobrevivido à primeira correção.
     */
    minhaPosicao: { type: Number, default: null },
  },
  data() {
    return {
      MOTIVOS,
      ranking: [],
      loading: true,
      error: null,
      denunciando: null,
      motivo: 'offensive',
      detalhe: '',
      enviando: false,
      recado: '',
    };
  },
  computed: {
    topo() {
      return this.ranking.slice(0, 5);
    },
    meuCla() {
      if (!this.meuClaId) return null;
      if (this.topo.some((c) => c.id === this.meuClaId)) return null;
      const dentro = this.ranking.find((c) => c.id === this.meuClaId);
      if (dentro) return dentro;

      /*
       * Fora das 50 linhas. A posição vem do servidor; o resto do que a linha
       * precisa, do que já se sabe. Esconder o clã aqui seria repetir o defeito
       * que este componente existe para corrigir.
       */
      if (!this.minhaPosicao) return null;
      return {
        id: this.meuClaId,
        position: this.minhaPosicao,
        name: this.$t('clan.rankingSeuCla'),
        level: null,
        pontuation: null,
        flag: null,
        country: null,
      };
    },
    visiveis() {
      return this.meuCla ? [...this.topo, this.meuCla] : this.topo;
    },
  },
  async mounted() {
    try {
      this.loading = true;
      // `GET /rankings/clans` do V2 — o líder já vem achatado (`leaderName`),
      // sem o `clan.leader.user.username` do populate do Strapi.
      this.ranking = await v2.rankingDeClas();
      this.loading = false;
    } catch (err) {
      this.error = this.$t('clan.rankingError');
      this.loading = false;
    }
  },
  methods: {
    formato(n) {
      return n === null ? '—' : new Intl.NumberFormat('pt-BR').format(n || 0);
    },

    abrirDenuncia(cla) {
      this.denunciando = cla;
      this.motivo = 'offensive';
      this.detalhe = '';
      this.recado = '';
    },

    /**
     * Envia a denúncia.
     *
     * O 409 — "você já denunciou este clã" — **não é erro**, e o texto diz
     * isso: uma pessoa denuncia uma vez por clã, e insistir não aumenta o peso
     * do caso. Tratá-lo como falha faria o jogador tentar de novo.
     */
    async enviarDenuncia() {
      this.enviando = true;
      this.recado = '';
      try {
        await v2.denunciarBandeira(this.$store.state.player.id, this.denunciando.id, {
          motivo: this.motivo,
          detalhe: this.detalhe,
        });
        this.recado = this.$t('clan.bandeira.denunciada');
        this.denunciando = null;
      } catch (error) {
        this.recado =
          error?.response?.status === 409
            ? this.$t('clan.bandeira.jaDenunciada')
            : error?.response?.data?.message || this.$t('clan.bandeira.falhou');
      } finally {
        this.enviando = false;
      }
    },
  },
};
</script>

<style scoped>
/* A faixa: uma tábua horizontal de quadro de avisos — madeira escura, letras claras. */
.faixa {
  border: 3px solid var(--madeira-escura);
  background: var(--madeira);
  padding: var(--e-2) var(--e-3);
  color: var(--papel-luz);
}

.faixa__rotulo {
  margin: 0 0 var(--e-2);
  display: flex;
  align-items: center;
  gap: var(--e-1);
  font-family: var(--fonte-display);
  font-size: var(--f-2);
}

.faixa__quieto {
  margin: 0;
  font-size: var(--f-1);
  color: var(--papel-sombra);
  font-style: italic;
}

.faixa__lista {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--e-2) var(--e-4);
}

.faixa__cla {
  display: flex;
  align-items: center;
  gap: var(--e-1);
  font-size: var(--f-1);
}

/* A bandeira pendurada: pequena, mas o suficiente para reconhecer de relance. */
.faixa__bandeira,
.faixa__sem-bandeira {
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
  object-fit: contain;
  image-rendering: pixelated;
}

.faixa__sem-bandeira {
  border: 1px dashed var(--papel-sombra);
  opacity: 0.4;
}

.faixa__posicao {
  color: var(--papel-sombra);
}

.faixa__cla--ouro .faixa__posicao {
  color: var(--ouro);
}

/* O seu clã: sublinhado a latão — sempre encontrável de relance. */
.faixa__cla--meu {
  border-bottom: 2px solid var(--latao);
}

.faixa__nome {
  font-family: var(--fonte-display);
}

.faixa__nivel,
.faixa__pontos {
  color: var(--papel-sombra);
}

.faixa__denunciar {
  background: none;
  border: 0;
  padding: 0;
  color: var(--papel-sombra);
  cursor: pointer;
}

.faixa__denunciar:hover {
  color: var(--ouro);
}

.faixa__denuncia {
  margin-top: var(--e-2);
  padding-top: var(--e-2);
  border-top: 2px dashed var(--madeira-escura);
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
}

.faixa__denuncia-alvo {
  margin: 0;
  font-size: var(--f-1);
}

.faixa__campo {
  padding: var(--e-1);
  border: 2px solid var(--madeira-escura);
  background: var(--papel);
  color: var(--tinta);
  font: inherit;
  font-size: var(--f-1);
}

.faixa__denuncia-botoes {
  display: flex;
  align-items: center;
  gap: var(--e-2);
}

.faixa__cancelar {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: var(--f-1);
  color: var(--papel-sombra);
  text-decoration: underline dashed;
  cursor: pointer;
}

.faixa__recado {
  margin: 0;
  font-size: var(--f-1);
  color: var(--papel-luz);
}
</style>
